import React from 'react';
import { AlertTriangle, FileText, Folder, Mail, Trash2 } from 'lucide-react';
import { Modal, ModalContent, ModalFooter } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui';
import { SpinnerIcon } from '../../../components/ui/Icons';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Z_INDEX } from '../../../lib/zLayers';

export interface TemplateFolderDeleteConfirmModalProps {
  isOpen: boolean;
  folderName: string;
  itemsToRemove: string[];
  deleting?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const TemplateFolderDeleteConfirmModal: React.FC<TemplateFolderDeleteConfirmModalProps> = ({
  isOpen,
  folderName,
  itemsToRemove,
  deleting = false,
  onClose,
  onConfirm,
}) => {
  const { colors } = useTheme();

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !deleting && onClose()}
      size="md"
      zIndex={Z_INDEX.modalHigh}
      closeOnEscape={!deleting}
    >
      <ModalContent>
        <div className="space-y-5 pt-1">
          <div className="flex flex-col items-center text-center sm:flex-row sm:items-start sm:text-left gap-4">
            <div
              className={cn(
                'flex h-12 w-12 shrink-0 items-center justify-center rounded-full',
                'bg-red-100 text-red-600 dark:bg-red-950/60 dark:text-red-400',
              )}
            >
              <Trash2 className="h-6 w-6" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className={cn('text-lg font-semibold tracking-tight', colors.text)}>
                Delete this folder?
              </h3>
              <p className={cn('mt-1 text-sm break-words', colors.textSecondary)}>
                You are about to permanently delete
              </p>
              <p
                className={cn(
                  'mt-1 text-sm font-semibold break-words',
                  'text-amber-800 dark:text-amber-200',
                )}
                title={folderName}
              >
                {folderName}
              </p>
            </div>
          </div>

          {itemsToRemove.length > 0 ? (
            <div
              className={cn(
                'rounded-xl border px-4 py-3',
                colors.border,
                'bg-gray-50/90 dark:bg-gray-900/40',
              )}
            >
              <p className={cn('text-xs font-semibold uppercase tracking-wider mb-2.5', colors.textMuted)}>
                What will be removed
              </p>
              <ul className="space-y-2">
                {itemsToRemove.map((item) => {
                  const isSubfolder = item.includes('subfolder');
                  const isDocument = item.includes('document');
                  const Icon = isSubfolder ? Folder : isDocument ? FileText : Mail;
                  return (
                    <li
                      key={item}
                      className={cn('flex items-center gap-2.5 text-sm font-medium', colors.text)}
                    >
                      <Icon
                        className={cn(
                          'h-4 w-4 shrink-0',
                          isSubfolder
                            ? 'text-amber-600 dark:text-amber-400'
                            : isDocument
                              ? 'text-blue-600 dark:text-blue-400'
                              : 'text-indigo-600 dark:text-indigo-400',
                        )}
                        aria-hidden
                      />
                      <span>{item}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <p className={cn('text-sm', colors.textMuted)}>This folder is empty and will be removed.</p>
          )}

          <div
            className={cn(
              'flex gap-3 rounded-xl border px-4 py-3',
              'border-red-200 bg-red-50/90 dark:border-red-900/50 dark:bg-red-950/30',
            )}
          >
            <AlertTriangle
              className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400 mt-0.5"
              aria-hidden
            />
            <p className="text-sm leading-relaxed text-red-800 dark:text-red-200">
              This action cannot be undone. All nested folders, templates, documents, and file
              attachments will be permanently deleted.
            </p>
          </div>
        </div>
      </ModalContent>
      <ModalFooter>
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={deleting}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className={cn(
              'w-full sm:w-auto font-semibold',
              'bg-red-600 text-white hover:bg-red-700',
              'focus:ring-red-500 dark:bg-red-600 dark:hover:bg-red-500',
            )}
          >
            {deleting ? (
              <>
                <SpinnerIcon className="w-4 h-4" />
                Deleting…
              </>
            ) : (
              'Delete permanently'
            )}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default TemplateFolderDeleteConfirmModal;
